import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { CheckboxCardSkeleton } from "@/components/CheckboxCard";
import { FormActions } from "@jbnl/shared-ui";
import { Field } from "@/components/ui/field";
import {
  Skeleton,
  SkeletonLine,
  SkeletonRegion,
} from "@/components/ui/skeleton";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { useSeizoenen } from "@/features/session/useSeizoenen";
import { huidigePeriode, vensterStatus } from "@/features/inschrijving/venster";
import { PloegWizard } from "@/features/inschrijving/PloegWizard";
import {
  useInschrijving,
  useInschrijvingActies,
  useKorpsPeriodes,
  type Inschrijving,
  type PloegInschrijving,
} from "@/features/inschrijving/useInschrijving";

/**
 * A page rather than a modal: two screens' worth of klassen and dates in a dialog scrolls inside
 * itself and hides the list it is a detour from. Both ids are in the URL, so a korpscontact can
 * send a colleague the ploeg rather than the way to find it.
 */
export function PloegPage() {
  const { t } = useTranslation(["inschrijving", "common"]);
  const { data: me, isPending: sessionLoading } = useMe();
  const { data: seizoenen } = useSeizoenen();
  const {
    korps: korpsId,
    ploeg: ploegId,
    periode: periodeId,
  } = useSearch({ from: "/ploeg" });
  const navigate = useNavigate();

  const seizoen = seizoenen?.[0];
  const { data: periodes } = useKorpsPeriodes(seizoen?.id);
  // Named by the caller, because two korps windows stand open at once and the first of them is
  // not the one the korps was looking at. The fallback is for a link made before it was named.
  const periode =
    periodes?.find((kandidaat) => kandidaat.id === periodeId) ??
    huidigePeriode(periodes);
  const venster = vensterStatus(periode);

  // One korps is not a choice: a korpscontact holds exactly one and never named it in the URL.
  const korps = me?.korpsen.find((k) => k.id === korpsId) ?? me?.korpsen[0];

  const {
    data: inschrijving,
    isPending,
    isError,
  } = useInschrijving(periode?.id, korps?.id);
  const acties = useInschrijvingActies(periode?.id, korps?.id);

  const terug = () =>
    void navigate({
      to: "/ploegen",
      search: { korps: korps?.id, periode: periode?.id },
    });

  if (sessionLoading) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  const ploeg = inschrijving?.ploegen.find((p) => p.ploegId === ploegId);

  // A ploeg id that no longer resolves is a ploeg somebody withdrew, or a link to another
  // korps's. Say so rather than silently opening an empty inschrijving form under its name.
  const kwijt = Boolean(ploegId) && Boolean(inschrijving) && !ploeg;

  return (
    <Shell name={me.name}>
      <div className="space-y-6">
        <div>
          <Link
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            to="/ploegen"
            search={{ korps: korps?.id, periode: periode?.id }}
          >
            <ChevronLeft className="size-4" />
            {t("pagina.terug")}
          </Link>

          <h1 className="text-2xl font-bold tracking-[-0.02em]">
            {ploeg ? ploeg.name : t("pagina.nieuweTitel")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {korps?.name}
            {periode
              ? ` · ${t("pagina.korpsMetPeriode", {
                  periode: periode.name,
                  venster: venster.label,
                })}`
              : ""}
          </p>
        </div>

        {!korps && (
          <Card>
            <CardHeader>
              <CardTitle>{t("pagina.geenKorps.title")}</CardTitle>
              <CardDescription>
                {t("pagina.geenKorps.description")}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {korps && isPending && periode && (
          <Card>
            <CardContent className="pt-6">
              <PloegWizardSkeleton />
            </CardContent>
          </Card>
        )}

        {isError && (
          <p className="text-sm text-destructive">{t("errors.ophalen")}</p>
        )}

        {kwijt && (
          <Card>
            <CardHeader>
              <CardTitle>{t("pagina.kwijt.title")}</CardTitle>
              <CardDescription>{t("pagina.kwijt.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={terug}>{t("pagina.terug")}</Button>
            </CardContent>
          </Card>
        )}

        {inschrijving && korps && !kwijt && (
          <PloegWizardKaart
            inschrijving={inschrijving}
            ploeg={ploeg}
            venster={venster}
            acties={acties}
            terug={terug}
          />
        )}
      </div>
    </Shell>
  );
}

/**
 * The wizard's props all read "the ploeg's, or a fresh inschrijving's" - kept together here
 * rather than as ternaries scattered through `PloegPage`, since every one of them answers the
 * same question about the same `ploeg`.
 */
function PloegWizardKaart({
  inschrijving,
  ploeg,
  venster,
  acties,
  terug,
}: {
  inschrijving: Inschrijving;
  ploeg: PloegInschrijving | undefined;
  venster: ReturnType<typeof vensterStatus>;
  acties: ReturnType<typeof useInschrijvingActies>;
  terug: () => void;
}) {
  const { t } = useTranslation(["inschrijving", "common"]);
  const { aanmelden, opslaan, intrekken } = acties;

  return (
    <Card>
      <CardContent className="pt-6">
        <PloegWizard
          // Keyed on the ploeg, so opening another one starts its own wizard at step one
          // rather than on whichever step the last one was left on.
          key={ploeg?.ploegInschrijvingId ?? "nieuw"}
          id={ploeg ? `ploeg-${ploeg.ploegId}` : "nieuwe-ploeg"}
          inschrijving={inschrijving}
          velden={
            ploeg
              ? {
                  name: ploeg.name === ploeg.defaultName ? "" : ploeg.name,
                  klasseIds: ploeg.klasseIds,
                  voorkeuren: ploeg.voorkeuren,
                  wilSamenOpEenDag: ploeg.wilSamenOpEenDag,
                  deeltVrVoertuig: ploeg.deeltVrVoertuig,
                }
              : undefined
          }
          standaardNaam={ploeg ? ploeg.defaultName : inschrijving.nextPloegName}
          submitLabel={
            ploeg ? t("common:actions.save") : t("pagina.inschrijvenLabel")
          }
          pending={ploeg ? opslaan.isPending : aanmelden.isPending}
          error={ploeg ? opslaan.error : aanmelden.error}
          gesloten={!venster.open}
          leadingAction={
            ploeg ? (
              /* The rows go for good: the audit trail records what went, but nothing puts
                it back. The trigger stays quiet, since red is for the action you came to do. */
              <ConfirmDelete
                trigger={
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive"
                    disabled={intrekken.isPending || !venster.open}
                  >
                    {intrekken.isPending
                      ? t("common:actions.busy")
                      : t("pagina.intrekken")}
                  </Button>
                }
                title={t("pagina.intrekkenTitle", { naam: ploeg.name })}
                description={t("pagina.intrekkenDescription")}
                confirmLabel={t("pagina.intrekkenConfirm", {
                  naam: ploeg.name,
                })}
                onConfirm={() =>
                  intrekken.mutate(ploeg.ploegId, { onSuccess: terug })
                }
              />
            ) : undefined
          }
          onSubmit={(velden) =>
            ploeg
              ? opslaan.mutate(
                  { ploegId: ploeg.ploegId, ...velden },
                  { onSuccess: terug },
                )
              : aanmelden.mutate(velden, { onSuccess: terug })
          }
        />

        {intrekken.error && (
          <p className="mt-3 text-sm text-destructive">
            {intrekken.error.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The wizard's first step before the inschrijving has arrived: the naam field, the klassen to
 * tick, and the row that carries it forward. Both steps open on this shape, so it is the one
 * worth standing in for.
 */
function PloegWizardSkeleton() {
  return (
    <SkeletonRegion className="space-y-5">
      <SkeletonLine className="w-24" />

      {/* A bar rather than the real label: a <label> pointing at no control is a control a
          screen reader can be told to move to and then cannot. */}
      <Field>
        <SkeletonLine className="w-44" />
        <Skeleton className="h-9 w-full" />
        <SkeletonLine className="w-3/4" />
      </Field>

      <ul className="grid gap-2 sm:grid-cols-2">
        {Array.from({ length: 4 }, (_, klasse) => (
          <li key={klasse}>
            <CheckboxCardSkeleton />
          </li>
        ))}
      </ul>

      <FormActions>
        <Skeleton className="h-9 w-28" />
      </FormActions>
    </SkeletonRegion>
  );
}
